import React, { useMemo, useState } from "react";
import { IMillNetworkDetailsContextProps, RangeHours, RangeLabelEnum } from "./MillNetworkDetailsContextInterfaces";
import { useTranslation } from "react-i18next";
import { IOption } from "components/MainSelect/MainSelectInterfaces";
import { initialState, MillNetworkDetailsContext } from "./MillNetworkDetailsContext";
import { SelectChangeEvent } from "@mui/material";
import { useParams } from "react-router-dom";
import { MillLocalserverEntity } from "entities/MillLocalserver/MillLocalserverEntity";
import { useTableOperations } from "components/MainTable/hooks/useTableOperations";

export const MillNetworkDetailsProvider: React.FC<IMillNetworkDetailsContextProps> = ({ children }) => {
    const { t } = useTranslation();
    const { millId } = useParams();

    const [rangeSelected, setRangeSelected] = useState(initialState.rangeSelected);

    const millIdMemoized = useMemo(() => (Boolean(millId) ? Number(millId) : 0), [millId]);

    const { handleRequestSort, handleChangeRowsPerPage, handleChangePage, order, orderBy, page, rowsPerPage } =
        useTableOperations();

    const queryTableOperationFilters = useMemo(
        () => ({
            limit: rowsPerPage,
            offset: page * rowsPerPage,
            order,
            orderBy,
        }),
        [page, rowsPerPage, order, orderBy],
    );

    const { statusReports, statusAverages, loading, refetch } = MillLocalserverEntity.GetLocalserverStatusDetails(
        millIdMemoized,
        rangeSelected,
        queryTableOperationFilters,
    );

    const rangeFilterOptions: IOption[] = useMemo(
        () =>
            Object.values(RangeHours).map((range) => ({
                id: range.value,
                name: t(`locations.mill_network_details.range_filters.${range.label}`, {
                    range: range.label === RangeLabelEnum.WEEK ? range.value / 168 : range.value,
                }),
            })),
        [t],
    );

    const handleRangeSelection = (event: SelectChangeEvent<number>) => {
        setRangeSelected(Number(event.target.value));
    };

    const providerValue = {
        rangeFilterOptions,
        rangeSelected,
        statusAverages,
        statusReports,
        loading,
        handleRangeSelection,
        refetch,
        statusReportsTableResources: {
            handleRequestSort,
            handleChangeRowsPerPage,
            handleChangePage,
            order,
            orderBy,
            page,
            rowsPerPage,
        },
    };

    return <MillNetworkDetailsContext.Provider value={providerValue}>{children}</MillNetworkDetailsContext.Provider>;
};
